import { useCallback, useEffect, useMemo } from 'react';

import {
  LocationDTO,
  LocationSelectFormElementAnswerSchema,
  LocationSelectFormElement as LocationSelectFormElementType,
  RecordState,
  withAssetLocation,
} from '@gearbelt/global';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { getAssetLocationQueryOptions, getLocationsQueryOptions } from '@/queries/locations';
import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useIsAWA } from '@/utils/awa';

import { ConfigLabel } from '../../config-label';
import { FieldShell } from '../../field-shell';
import { DropdownBase, DropdownOption } from './dropdown-base';

const toOption = (location: Pick<LocationDTO, 'id' | 'name'>): DropdownOption => ({ value: location.id, label: location.name });

function useLocationOptions({ assetLocationId, savedId }: { assetLocationId?: string; savedId?: string | null }) {
  const { isAWA } = useIsAWA();

  const { data: locationsData, isError } = useQuery(getLocationsQueryOptions({ limit: 100, isAWA, filters: { state: RecordState.Active } }));
  const { data: assetLocations } = useQuery(getAssetLocationQueryOptions({ assetLocationId, isAWA }));

  const options = useMemo(() => {
    return withAssetLocation(locationsData?.data ?? [], assetLocations?.[0]).map(toOption);
  }, [locationsData, assetLocations]);

  const isSavedListed = !savedId || options.some((option) => option.value === savedId);
  // No state filter, so a saved location that has since been archived still shows its name.
  const { data: savedLocationData } = useQuery(
    getLocationsQueryOptions({ limit: 1, isAWA, filters: { fields: { id: [savedId ?? ''] } }, enabled: !!savedId && !!locationsData && !isSavedListed }),
  );
  const selectedOptions = useMemo(() => (savedLocationData?.data ?? []).map(toOption), [savedLocationData]);

  const loadOptions = useCallback(
    async (search: string) => {
      const { data } = await (await (isAWA ? AWAApiV2Service : ApiService).GetLocations({ limit: 100, filters: { state: RecordState.Active, search } })).json();
      return (data ?? []).map(toOption);
    },
    [isAWA],
  );

  return { options, selectedOptions, loadOptions, isError };
}

function usePrefillAssetLocation({
  element,
  value,
  assetLocationId,
  options,
  onPrefill,
}: {
  element: LocationSelectFormElementType;
  value: string | null | undefined;
  assetLocationId?: string;
  options: DropdownOption[];
  onPrefill?: (value: string) => void;
}) {
  const populateLastLocation = element.config?.populateLastLocation ?? true;

  // The locations load async, so this lands after mount and would otherwise look
  // like an unprovoked edit.
  useEffect(() => {
    if (populateLastLocation && !value && assetLocationId && options.some((option) => option.value === assetLocationId)) {
      onPrefill?.(assetLocationId);
    }
  }, [populateLastLocation, value, assetLocationId, options, onPrefill]);
}

export const LocationSelectFormElement = ({
  element,
  value,
  onChange,
  onBlur,
  readonly,
  hideLabel,
  error,
  assetLocationId,
  onPrefill,
}: {
  element: LocationSelectFormElementType;
  value: z.infer<typeof LocationSelectFormElementAnswerSchema>['value'];
  onChange: (value: z.infer<typeof LocationSelectFormElementAnswerSchema>['value']) => void;
  onBlur?: () => void;
  readonly?: boolean;
  hideLabel?: boolean;
  error?: string;
  assetLocationId?: string;
  /**
   * Seed the asset's location. Separate from `onChange` because a default is not
   * a user edit: routing it through `onChange` marks the form dirty and prompts
   * "Save changes?" on a record nobody touched.
   */
  onPrefill?: (value: string) => void;
}) => {
  const { options, selectedOptions, loadOptions, isError } = useLocationOptions({ assetLocationId, savedId: value });
  usePrefillAssetLocation({ element, value, assetLocationId, options, onPrefill });

  return (
    <FieldShell label={element.label} required={element.required} hideLabel={hideLabel} error={error}>
      <DropdownBase
        id={element.id}
        placeholder={`Select ${element.label}`}
        multiple={false}
        options={options}
        selectedOptions={selectedOptions}
        loadOptions={loadOptions}
        value={value}
        onChange={(next) => onChange(Array.isArray(next) ? (next[0] ?? null) : next)}
        onBlur={onBlur}
        readonly={readonly}
        isError={isError}
        invalid={!!error}
      />
    </FieldShell>
  );
};

export function LocationSelectFormElementConfig({
  element,
  onChange,
}: {
  element: LocationSelectFormElementType;
  onChange?: (updates: Partial<LocationSelectFormElementType>) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>
      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>
      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox
          checked={element.config?.populateLastLocation ?? true}
          onCheckedChange={(checked) => onChange?.({ config: { populateLastLocation: !!checked } })}
        />
        <span>Populate last location</span>
      </ConfigLabel>
    </div>
  );
}
